/**
 * The canonical vocabulary.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  WHY THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────
 *
 * The owner reported "a lot of inconsistencies in the UI", and named one:
 * *"we are looking at only crypto market, so why is 'equity' appearing in
 * some places?"*
 *
 * He is right that it is confusing and the confusion is the data that
 * matters. "Equity" is correct trading English — it means the value of the
 * account, not a share in a company — but in a **crypto-only** product the
 * word arrives carrying the equities market with it, and a term that has to
 * be explained is a term that will be misread at 3am.
 *
 * So: **one label per concept, decided here, used everywhere.** A component
 * that invents its own wording for a concept in this list is the bug.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  THE RULING ON "EQUITY"
 * ─────────────────────────────────────────────────────────────────────
 *
 *   The scalar — what the account is worth right now
 *       -> **Account Value**            (`TERMS.accountValue`)
 *
 *   The series — that number plotted over time
 *       -> **Account Value Curve**      (`TERMS.accountValueCurve`)
 *          The React component is still called `EquityCurve`, because
 *          "equity curve" is the universal name for the *chart* and every
 *          reference the owner will ever read calls it that. The component
 *          name is jargon for engineers; the label is English for him.
 *
 *   The API field is `equity` and stays `equity`. Renaming a wire contract
 *   to fix a label would be a schema migration in service of a caption.
 *   The translation happens at the point of rendering — here.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  WORDS THAT ARE BANNED IN THIS PRODUCT
 * ─────────────────────────────────────────────────────────────────────
 *
 *   "shares", "stock", "ticker"        -> units / coins / pair, symbol
 *   "market open", "session", "close"  -> crypto is 24/7/365; there is no
 *                                         open, no close, no weekend and no
 *                                         holiday. A daily boundary is a
 *                                         **UTC day**, and it is arbitrary.
 *   "252 trading days"                 -> 365. A wrong annualisation
 *                                         constant silently corrupts every
 *                                         Sharpe, Sortino, CAGR and
 *                                         volatility figure by ~20%.
 *   "€", "$"                           -> the quote asset code. USDT is not
 *                                         USD. See `lib/format`.
 */

/**
 * Canonical labels.
 *
 * Sentence case as written here. Components that need a heading shout it
 * with CSS (`uppercase`), not by storing a second spelling of the word.
 */
export const TERMS = {
  // ── Account ─────────────────────────────────────────────────────────
  /** Total value of the account right now. Never "equity" in the UI. */
  accountValue: 'Account Value',
  /** Highest account value ever reached. The denominator of drawdown. */
  peakAccountValue: 'Peak Account Value',
  /** The account value plotted over time. */
  accountValueCurve: 'Account Value Curve',
  /** Cash not committed to a position. */
  available: 'Available',
  /** Fall from the peak, as a percentage of the peak. */
  drawdown: 'Drawdown',
  /** The worst drawdown in the period. The number that predicts quitting. */
  maxDrawdown: 'Max Drawdown',

  // ── Risk ────────────────────────────────────────────────────────────
  /** What every open position would lose if every stop filled at its level. */
  openRisk: 'Open Risk',
  /** Loss budget for the UTC day. */
  dailyLossLimit: 'Daily Loss Limit',
  /** Budget left before trading stops for the day. */
  lossBudgetRemaining: 'Loss Budget Left',
  /** Risk on one entry, as a percentage of account value. */
  riskPerTrade: 'Risk per Trade',
  /** The resting exchange order that closes a losing position. */
  stop: 'Stop',
  /** Stops resting on the venue cover the whole position size. */
  protected: 'Stop on Venue',
  /** They do not. There is no partial state — see `toneForProtection`. */
  unprotected: 'Unprotected',

  // ── Instrument ──────────────────────────────────────────────────────
  /** `BTCUSDT`. Never "ticker" — that is an equities word. */
  symbol: 'Symbol',
  /** The same thing said the long way, when explaining. Never "instrument". */
  pair: 'Pair',
  /** `BTC` in `BTCUSDT` — the thing you own. Never "shares". */
  baseAsset: 'Base asset',
  /** `USDT` in `BTCUSDT` — the thing you are priced in. */
  quoteAsset: 'Quote asset',
  /** How much of the base asset. Fractional. Never "shares", never "size". */
  quantity: 'Quantity',
  /** Bar interval. Never "period" — KLineChart's word, not ours. */
  timeframe: 'Timeframe',

  // ── Result ──────────────────────────────────────────────────────────
  /** Realised profit and loss, after fees. Always "P&L", never "PNL". */
  pnl: 'P&L',
  /** Open-position P&L. Never "paper profit" — `paper` means the venue. */
  unrealised: 'Unrealised P&L',
  /** Result in units of the risk taken. The first number to read. */
  rMultiple: 'R-multiple',
  /** Average R across trades. */
  expectancy: 'Expectancy',
  /** Exchange fees paid. */
  fees: 'Fees',

  // ── Time ────────────────────────────────────────────────────────────
  /**
   * A calendar day in UTC.
   *
   * Say "UTC day", never "trading day" or "session": crypto has neither,
   * and the boundary is a bookkeeping convention rather than a market event.
   */
  utcDay: 'UTC day',
} as const;

export type TermKey = keyof typeof TERMS;

/**
 * Environment labels.
 *
 * `paper` deliberately reads "Paper (simulated)" in prose. On its own,
 * "paper" is the one word in this product that a tired person could read as
 * "paper profit" — i.e. as real money.
 */
export const ENVIRONMENT_LABELS = {
  live: 'Live — real money',
  paper: 'Paper — simulated',
  testnet: 'Testnet — simulated',
} as const;
